import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout,
  Card,
  Typography,
  Space,
  Button,
  QRCode,
  Row,
  Col,
  Tag,
  Segmented,
  Form,
  Input,
  InputNumber,
  Select,
  Modal,
  Spin,
  Empty,
  message,
  Statistic,
  Divider,
  Alert,
  DatePicker,
} from 'antd';
import {
  CopyOutlined,
  ShareAltOutlined,
  GiftOutlined,
  QrcodeOutlined,
  LinkOutlined,
  EyeInvisibleOutlined,
  CheckOutlined,
  WalletOutlined,
  BarChartOutlined,
  TeamOutlined,
  DollarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { hapticFeedbackImpactOccurred, copyTextToClipboard } from '@telegram-apps/sdk-react';
import apiService from '../services/ApiService';
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const BANKS = [
  'Сбербанк', 'ВТБ', 'Тинькофф', 'Альфа-Банк', 'Газпромбанк',
  'Открытие', 'Россельхозбанк', 'Райффайзенбанк', 'МКБ', 'Другой',
];

const ReferralPage = () => {
  const navigate = useNavigate();
  const { user, t } = useApp();
  const [form] = Form.useForm();

  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);

  const [settings, setSettings] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [period, setPeriod] = useState('month');
  const [customRange, setCustomRange] = useState(null);

  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);

  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawMethod, setWithdrawMethod] = useState('sbp');
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);

  const statusLabels = {
    pending:  { text: t('referral_status_pending'),  color: 'orange' },
    approved: { text: t('referral_status_approved'), color: 'green' },
    rejected: { text: t('referral_status_rejected'), color: 'red' },
  };

  const periodOptions = [
    { value: 'today',  label: t('referral_period_today') },
    { value: 'week',   label: t('referral_period_week') },
    { value: 'month',  label: t('referral_period_month') },
    { value: 'custom', label: t('referral_period_custom') },
  ];

  // Back button
  useEffect(() => {
    const bb = window.Telegram?.WebApp?.BackButton;
    if (bb) {
      bb.show();
      const handler = () => navigate('/');
      bb.onClick(handler);
      return () => { try { bb.offClick(handler); bb.hide(); } catch (_) {} };
    }
  }, [navigate]);

  // Load settings
  useEffect(() => {
    apiService.getReferralSettings()
      .then(setSettings)
      .catch(() => setSettings({ level1_percent: 30, level2_percent: 20, min_withdrawal: 100 }));
  }, []);

  // Load stats
  const loadStats = useCallback(async () => {
    if (!user) return;
    setStatsLoading(true);
    try {
      let dateFrom = null;
      let dateTo = null;
      if (period === 'custom' && customRange) {
        dateFrom = customRange[0].format('YYYY-MM-DD');
        dateTo = customRange[1].format('YYYY-MM-DD');
      }
      const data = await apiService.getReferralStats(user.user_id, period, dateFrom, dateTo);
      setStatsData(data);
    } catch (e) {
      console.error('Stats error:', e);
    } finally {
      setStatsLoading(false);
    }
  }, [user, period, customRange]);

  useEffect(() => {
    if (period !== 'custom') loadStats();
  }, [period, loadStats]);

  // Load withdrawals
  const loadWithdrawals = useCallback(async () => {
    if (!user) return;
    setWithdrawalsLoading(true);
    try {
      const data = await apiService.getUserWithdrawals(user.user_id);
      setWithdrawals(data);
    } catch (e) {
      setWithdrawals([]);
    } finally {
      setWithdrawalsLoading(false);
    }
  }, [user]);

  useEffect(() => { loadWithdrawals(); }, [loadWithdrawals]);

  const copyReferralLink = async () => {
    if (user?.referal_link) {
      try {
        await copyTextToClipboard(user.referal_link);
        setCopied(true);
        hapticFeedbackImpactOccurred('light');
        setTimeout(() => setCopied(false), 2000);
      } catch (_) {
        hapticFeedbackImpactOccurred('heavy');
      }
    }
  };

  const shareReferralLink = async () => {
    if (user?.referal_link) {
      const shareText = `${t('referral_share_brand')} ${user.referal_link}`;
      if (navigator.share) {
        try {
          await navigator.share({ title: 'VPNCH1K', text: shareText, url: user.referal_link });
        } catch (_) {}
      } else {
        copyReferralLink();
      }
    }
  };

  const onWithdrawSubmit = async (values) => {
    setWithdrawSubmitting(true);
    try {
      const payload = {
        user_id: user.user_id,
        amount: values.amount,
        method: withdrawMethod,
        phone: withdrawMethod === 'sbp' ? values.phone : null,
        bank: withdrawMethod === 'sbp' ? values.bank : null,
        card_number: withdrawMethod === 'card' ? values.card_number : null,
      };
      await apiService.createWithdrawal(payload);
      message.success(t('referral_submit_success'));
      setWithdrawModalOpen(false);
      form.resetFields();
      loadWithdrawals();
      window.location.reload();
    } catch (e) {
      const errMsg = e.message || t('referral_submit_error');
      message.error(errMsg.includes('400') ? t('referral_submit_error') : errMsg);
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  // Chart keys must match data object keys
  const incomeKey = t('referral_chart_income_key');
  const usersKey  = t('referral_chart_users_key');

  const chartData = statsData?.points?.map((p) => ({
    date: p.date,
    [incomeKey]: p.income,
    [usersKey]: p.users,
  })) || [];

  const balance = user?.balance || 0;
  const minWithdrawal = settings?.min_withdrawal || 100;

  return (
    <Layout>
      <Content style={{ padding: '16px', background: 'transparent' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>

          {/* Баланс и кнопка вывода */}
          <Card>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Space direction="vertical" size={2}>
                  <Text type="secondary" style={{ fontSize: 13 }}>{t('referral_partner_balance')}</Text>
                  <Title level={2} style={{ margin: 0, color: 'var(--tg-theme-button-color, #1677ff)' }}>
                    {balance} ₽
                  </Title>
                </Space>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<WalletOutlined />}
                  disabled={balance < minWithdrawal}
                  onClick={() => setWithdrawModalOpen(true)}
                >
                  {t('referral_withdraw_btn')}
                </Button>
              </Col>
            </Row>
            {balance < minWithdrawal && (
              <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                {t('referral_min_amount', { amount: minWithdrawal })}
              </Text>
            )}
          </Card>

          {/* Условия программы */}
          {settings && (
            <Card title={<><GiftOutlined /> {t('referral_program_title')}</>}>
              <Row gutter={12}>
                <Col span={12}>
                  <Card size="small" style={{ textAlign: 'center', background: 'var(--ant-color-bg-layout)' }}>
                    <Statistic
                      title={t('referral_tier1_label')}
                      value={settings.level1_percent}
                      suffix="%"
                      valueStyle={{ color: 'var(--tg-theme-button-color, #1677ff)', fontWeight: 'bold' }}
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {t('referral_tier1_desc')}
                    </Text>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small" style={{ textAlign: 'center', background: 'var(--ant-color-bg-layout)' }}>
                    <Statistic
                      title={t('referral_tier2_label')}
                      value={settings.level2_percent}
                      suffix="%"
                      valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {t('referral_tier2_desc')}
                    </Text>
                  </Card>
                </Col>
              </Row>
            </Card>
          )}

          {/* Реферальная ссылка */}
          {user?.referal_link && (
            <Card
              title={<><LinkOutlined /> {t('referral_link_title')}</>}
              extra={
                <Button
                  type="text"
                  icon={showQR ? <EyeInvisibleOutlined /> : <QrcodeOutlined />}
                  onClick={() => setShowQR(!showQR)}
                />
              }
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Card
                  hoverable
                  onClick={copyReferralLink}
                  style={{ cursor: 'pointer', borderColor: copied ? '#52c41a' : undefined }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                        {t('referral_link_copy_hint')}
                      </Text>
                      <Text
                        code
                        style={{ fontSize: 13, wordBreak: 'break-all', fontFamily: 'Monaco,Menlo,monospace', display: 'block', lineHeight: 1.4 }}
                      >
                        {user.referal_link}
                      </Text>
                    </div>
                    <div style={{ flexShrink: 0, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {copied
                        ? <CheckOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                        : <CopyOutlined style={{ fontSize: 20, color: 'var(--tg-theme-button-color, #1677ff)' }} />
                      }
                    </div>
                  </div>
                  {copied && (
                    <Text style={{ fontSize: 12, marginTop: 8, display: 'block', textAlign: 'center', color: '#52c41a' }}>
                      {t('referral_copied')}
                    </Text>
                  )}
                </Card>

                <Row gutter={8}>
                  <Col span={12}>
                    <Button block type="primary" icon={<ShareAltOutlined />} onClick={shareReferralLink}>
                      {t('referral_share')}
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button block icon={<QrcodeOutlined />} onClick={() => setShowQR(!showQR)}>
                      {showQR ? t('referral_qr_hide') : t('referral_qr_show')}
                    </Button>
                  </Col>
                </Row>

                {showQR && (
                  <Card size="small">
                    <Space direction="vertical" size="middle" align="center" style={{ width: '100%' }}>
                      <QRCode value={user.referal_link} size={180} status="active" bordered={false} />
                      <Text type="secondary" style={{ textAlign: 'center' }}>
                        {t('referral_scan_hint')}
                      </Text>
                    </Space>
                  </Card>
                )}
              </Space>
            </Card>
          )}

          {/* Статистика */}
          <Card
            title={<><BarChartOutlined /> {t('referral_stats_title')}</>}
            extra={
              <Segmented
                size="small"
                options={periodOptions}
                value={period}
                onChange={setPeriod}
              />
            }
          >
            {period === 'custom' && (
              <Space style={{ marginBottom: 12 }}>
                <RangePicker
                  size="small"
                  onChange={setCustomRange}
                  maxDate={dayjs()}
                />
                <Button size="small" type="primary" onClick={loadStats} disabled={!customRange}>
                  {t('referral_apply')}
                </Button>
              </Space>
            )}

            {statsData && (
              <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col span={12}>
                  <Statistic
                    title={<><DollarOutlined /> {t('referral_income_title')}</>}
                    value={statsData.total_income}
                    suffix="₽"
                    valueStyle={{ fontSize: 20, color: 'var(--tg-theme-button-color, #1677ff)' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={<><TeamOutlined /> {t('referral_attracted_title')}</>}
                    value={statsData.total_users}
                    suffix={t('referral_people_suffix')}
                    valueStyle={{ fontSize: 20, color: '#52c41a' }}
                  />
                </Col>
              </Row>
            )}

            {statsLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin />
              </div>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--ant-color-border)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => v.slice(5)}
                  />
                  <YAxis yAxisId="income" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="users" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ background: 'var(--ant-color-bg-elevated)', border: '1px solid var(--ant-color-border)', borderRadius: 8, fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line
                    yAxisId="income"
                    type="monotone"
                    dataKey={incomeKey}
                    stroke="var(--tg-theme-button-color, #1677ff)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="users"
                    type="monotone"
                    dataKey={usersKey}
                    stroke="#52c41a"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty description={t('referral_no_stats')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          {/* История выводов */}
          <Card title={<><ClockCircleOutlined /> {t('referral_history_title')}</>}>
            {withdrawalsLoading ? (
              <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
            ) : withdrawals.length === 0 ? (
              <Empty description={t('referral_no_withdrawals')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {withdrawals.map((w) => (
                  <Card key={w.id} size="small" style={{ background: 'var(--ant-color-bg-layout)' }}>
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Space direction="vertical" size={2}>
                          <Text strong>{w.amount} ₽</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {w.method === 'sbp'
                              ? `${t('referral_method_sbp')} · ${w.phone}`
                              : `${t('referral_method_card_short')} · ${w.card_number}`}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {new Date(w.created_at).toLocaleDateString()}
                          </Text>
                        </Space>
                      </Col>
                      <Col>
                        <Space direction="vertical" size={2} align="end">
                          <Tag color={statusLabels[w.status]?.color}>
                            {statusLabels[w.status]?.text}
                          </Tag>
                          {w.rejection_reason && (
                            <Text type="danger" style={{ fontSize: 11 }}>
                              {w.rejection_reason}
                            </Text>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </Space>
            )}
          </Card>

        </Space>
      </Content>

      {/* Модальное окно вывода */}
      <Modal
        title={<><WalletOutlined /> {t('referral_modal_title')}</>}
        open={withdrawModalOpen}
        onCancel={() => { setWithdrawModalOpen(false); form.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onWithdrawSubmit}>
          <Alert
            message={t('referral_available_balance', { amount: balance })}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item label={t('referral_method_label')} required>
            <Segmented
              block
              options={[
                { value: 'sbp',  label: t('referral_method_sbp') },
                { value: 'card', label: t('referral_method_card_full') },
              ]}
              value={withdrawMethod}
              onChange={(v) => { setWithdrawMethod(v); form.resetFields(['phone', 'bank', 'card_number']); }}
            />
          </Form.Item>

          <Form.Item
            name="amount"
            label={t('referral_amount_label')}
            rules={[
              { required: true, message: t('referral_val_amount_req') },
              { type: 'number', min: minWithdrawal, message: t('referral_val_amount_min', { amount: minWithdrawal }) },
              { type: 'number', max: balance, message: t('referral_val_amount_max') },
            ]}
          >
            <InputNumber
              min={minWithdrawal}
              max={balance}
              placeholder={t('referral_val_amount_min', { amount: minWithdrawal })}
              style={{ width: '100%' }}
              addonAfter="₽"
            />
          </Form.Item>

          {withdrawMethod === 'sbp' && (
            <>
              <Form.Item
                name="phone"
                label={t('referral_phone_label')}
                rules={[
                  { required: true, message: t('referral_val_phone_req') },
                  { pattern: /^\+?[0-9]{10,15}$/, message: t('referral_val_phone_invalid') },
                ]}
              >
                <Input placeholder="+7 999 123-45-67" />
              </Form.Item>
              <Form.Item
                name="bank"
                label={t('referral_bank_label')}
                rules={[{ required: true, message: t('referral_val_bank_req') }]}
              >
                <Select placeholder={t('referral_bank_placeholder')}>
                  {BANKS.map((b) => <Option key={b} value={b}>{b}</Option>)}
                </Select>
              </Form.Item>
            </>
          )}

          {withdrawMethod === 'card' && (
            <Form.Item
              name="card_number"
              label={t('referral_card_label')}
              rules={[
                { required: true, message: t('referral_val_card_req') },
                { pattern: /^[0-9]{16}$/, message: t('referral_val_card_invalid') },
              ]}
            >
              <Input placeholder="1234567890123456" maxLength={16} />
            </Form.Item>
          )}

          <Divider />

          <Row gutter={8}>
            <Col span={12}>
              <Button block onClick={() => { setWithdrawModalOpen(false); form.resetFields(); }}>
                {t('cancel')}
              </Button>
            </Col>
            <Col span={12}>
              <Button block type="primary" htmlType="submit" loading={withdrawSubmitting}>
                {t('referral_submit')}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Layout>
  );
};

export default ReferralPage;
